import React, {
  useCallback,
  useState,
  useMemo,
  useRef,
  useEffect,
  forwardRef,
} from 'react';
import noop from 'lodash/noop';
import cx from 'classnames';
import { format } from 'date-fns';
import { TextArea } from 'components/form';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { useResponsive } from 'hooks/useResponsive';
import { formatDate } from 'utils/format';
import { DateSelector } from './DateSelector.component';
import styles from './DatePicker.module.scss';

type DateSelectorProps = {
  label?: string;
  minDate?: Date;
  maxDate?: Date;
  hasError?: boolean | null;
  helperText?: string;
  placeholder?: string;
  isOnboarding?: boolean | null;
  isFutureDate?: boolean | null;
};

export interface DateSelectorValues {
  id: string;
  label: string;
  value: number | null | undefined;
  isYear?: boolean | null;
}

type DatePickerProps = DateSelectorProps & {
  value: Date | null;
  onChange: (value: Date) => void;
  preselectMinDate?: boolean;
  className?: string;
  dateSelectorClassName?: string;
  dateInputClassName?: string;
  scrollToDropdown?: () => void;
  showOnlyYear?: boolean;
};

export const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const DatePicker = forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      label,
      value,
      onChange,
      minDate,
      hasError,
      helperText,
      preselectMinDate,
      placeholder,
      className,
      dateSelectorClassName,
      dateInputClassName,
      isOnboarding,
      scrollToDropdown,
      isFutureDate,
      showOnlyYear = false,
    },
    ref
  ) => {
    const [isMonthDropdownOpen, setIsMonthDropdownOpen] =
      useState<boolean>(false);
    const [isYearDropdownOpen, setIsYearDropdownOpen] =
      useState<boolean>(false);
    const [selectedYear, setSelectedYear] = useState<DateSelectorValues | null>(
      null
    );
    const dateContainerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLUListElement>(null);

    const [selectedMonth, setSelectedMonth] =
      useState<DateSelectorValues | null>(null);
    const contentClassNames = cx(
      {
        [styles['date-input']]: true,
      },
      dateInputClassName
    );
    const screens = useResponsive();

    useEffect(() => {
      const handleScroll = () => {
        screens.sm && updateDropdownMaxHeight();
      };
      window?.addEventListener('scroll-top', handleScroll);
      return () => {
        window?.removeEventListener('scroll-top', handleScroll);
      };
    }, [screens.sm]);

    const onChangeMonth = useCallback(
      (userSelectedMonth: DateSelectorValues) => {
        setSelectedMonth(userSelectedMonth);
        setIsMonthDropdownOpen(false);
      },
      []
    );

    // Generates year options for the DateSelector
    const yearOptions = useMemo(() => {
      const currentYear = new Date().getFullYear();

      const futureYearsCount = isFutureDate ? 10 : 0;
      const pastYearsCount = 40;

      return [
        ...(isFutureDate
          ? Array.from({ length: futureYearsCount }, (_, i) => ({
              id: (currentYear + futureYearsCount - i).toString(),
              label: (currentYear + futureYearsCount - i).toString(),
              value: currentYear + futureYearsCount - i,
              isYear: true,
            }))
          : []),
        ...Array.from({ length: pastYearsCount }, (_, i) => ({
          id: (currentYear - i).toString(),
          label: (currentYear - i).toString(),
          value: currentYear - i,
          isYear: true,
        })),
      ];
    }, [isFutureDate]);

    const updateDropdownMaxHeight = () => {
      if (dropdownRef.current && screens.sm) {
        const dropdownRect = dropdownRef.current.getBoundingClientRect();
        const maxHeight = window.innerHeight - dropdownRect.top - 12;
        dropdownRef.current.style.maxHeight = `${maxHeight}px`;
        dropdownRef.current.style.overflow = 'auto';
      }
    };
    // Handlers for year and month changes
    const onChangeYear = useCallback((userSelectedYear: DateSelectorValues) => {
      setSelectedYear(userSelectedYear);
      setIsMonthDropdownOpen(true);
      setIsYearDropdownOpen(false);
    }, []);

    // Generates month options for the DateSelector
    const monthOptions = useMemo(
      () =>
        months.map((month, index) => ({
          id: month,
          label: month,
          value: index,
          isYear: false,
        })),
      []
    );

    useOnClickOutside({
      ref: dateContainerRef,
      onOutsideClick: () => {
        setIsYearDropdownOpen(false);
        setIsMonthDropdownOpen(false);
      },
    });

    const toggleDropdown = () => {
      if (!isYearDropdownOpen && !isMonthDropdownOpen) {
        setIsMonthDropdownOpen(true);
      } else if (!isYearDropdownOpen || !isMonthDropdownOpen) {
        return;
      }
    };

    const dateLabel = useMemo(() => {
      if (value && selectedMonth && selectedYear && !showOnlyYear) {
        const selectedDate = formatDate(new Date(value));
        if (preselectMinDate && minDate) {
          const startDate = format(new Date(minDate), 'MMMM yyyy');
          return `${startDate} - ${selectedDate}`;
        } else {
          return selectedDate;
        }
      } else if (value && showOnlyYear) {
        return value.getFullYear() || '';
      } else {
        return '';
      }
    }, [value, preselectMinDate, minDate, selectedMonth, selectedYear]);

    // Handler for date change
    const onDateChange = useCallback(
      (option) => {
        let year;
        let month;
        if (option.isYear) {
          year = option?.value ?? new Date().getFullYear();
          month = selectedMonth?.value ?? new Date().getMonth();
        } else {
          year = selectedYear?.value ?? new Date().getFullYear();
          month = option?.value ?? new Date().getMonth();
        }

        onChange(new Date(year, month));
      },
      [selectedYear, selectedMonth, onChange]
    );

    useEffect(() => {
      if (value) {
        const date = new Date(value);
        const yearValue = date.getFullYear();
        const monthValue = date.getMonth();

        setSelectedYear({
          id: yearValue.toString(),
          label: yearValue.toString(),
          value: yearValue,
          isYear: true,
        });
        setSelectedMonth({
          id: months[monthValue],
          label: months[monthValue],
          value: monthValue,
          isYear: false,
        });
      }
    }, [value]);

    useEffect(() => {
      if ((isYearDropdownOpen || isMonthDropdownOpen) && screens.sm) {
        scrollToDropdown && scrollToDropdown();
      }
    }, [isYearDropdownOpen, isMonthDropdownOpen, screens.sm]);

    return (
      <>
        <div className={cx(styles.container, className)} ref={ref}>
          <div
            className={cx(styles['date-picker'], {
              [styles.range]: minDate && preselectMinDate,
            })}
          >
            <TextArea
              value={dateLabel}
              onChange={noop}
              label={label}
              readOnly
              isValid={!hasError}
              helperText={helperText}
              rows={1}
              iconName={
                isMonthDropdownOpen || isYearDropdownOpen
                  ? 'arrow-up'
                  : 'icon_arrow-down'
              }
              dateSelectIcon={true}
              onClick={toggleDropdown}
              textAreaClassName={contentClassNames}
              placeholder={placeholder}
              onKeyDown={(e) => e.preventDefault()}
            />
          </div>

          <div
            className={cx(styles['date-range-selector'], dateSelectorClassName)}
            ref={dateContainerRef}
          >
            {isYearDropdownOpen && (
              <DateSelector
                options={yearOptions}
                onChangeYear={onChangeYear}
                onChangeMonth={onChangeMonth}
                onDateChange={onDateChange}
                onChangeMonthDropdown={setIsMonthDropdownOpen}
                onChangeYearDropdown={setIsYearDropdownOpen}
                selectedYear={selectedYear?.value}
                selectedMonth={selectedMonth?.label}
                userSelectedValue={selectedYear?.label}
                isYearDropdownOpen={isYearDropdownOpen}
                className={styles['date-selector']}
                isOnboarding={isOnboarding}
                isMonthDropdownOpen={isMonthDropdownOpen}
                ref={dropdownRef}
              />
            )}
            {isMonthDropdownOpen && (
              <DateSelector
                options={monthOptions}
                onChangeYear={onChangeYear}
                onChangeMonth={onChangeMonth}
                onDateChange={onDateChange}
                onChangeMonthDropdown={setIsMonthDropdownOpen}
                onChangeYearDropdown={setIsYearDropdownOpen}
                selectedYear={selectedYear?.value}
                selectedMonth={selectedMonth?.label}
                userSelectedValue={selectedMonth?.label}
                isYearDropdownOpen={isYearDropdownOpen}
                isMonthDropdownOpen={isMonthDropdownOpen}
                className={styles['date-selector']}
                isOnboarding={isOnboarding}
                ref={dropdownRef}
              />
            )}
          </div>
        </div>
      </>
    );
  }
);
